// src/pages/BookSuccess.jsx
//
// NOTE: this assumes a router path like /articles/success — adjust the
// route you register this on (and the successUrl/cancelUrl in
// api/create-book-checkout.js) to match wherever your book store actually
// lives.
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import INFO from "../data/user";
import { sendBookPurchaseConfirmation } from "../utils/bookNotifications";

import "./styles/events.css";
import "./styles/eventSuccess.css";

// add near the top
import jsPDF from "jspdf";

const API_BASE = "https://jabuzwane.vercel.app/api";
const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 48; // ~2 minutes

// ── Embedded styles — matches the neutral catalog look used on the Books
// page (src/pages/articles.jsx). Kept inline, same as that page's BOOKS_CSS,
// so this page's look doesn't depend on whatever's in events.css /
// eventSuccess.css and can't silently drift out of sync with those files.
const ES_CSS = `
.es-page {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  background: var(--page-tint);
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 20px;
}

.es-container {
  width: 100%;
  max-width: 460px;
  margin-top: 80px;
}

/* ── loading / interim states ── */

.es-status {
  background: #ffffff;
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 40px 28px;
  text-align: center;
  box-shadow: 0 1px 2px rgba(20, 22, 26, 0.04), 0 6px 16px rgba(20, 22, 26, 0.05);
}

.es-status h2 {
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-color);
  margin: 16px 0 6px;
}

.es-status p {
  font-size: 14px;
  color: var(--secondary-color);
  line-height: 1.6;
  margin: 0;
}

.es-status--error h2 {
  color: var(--primary-color);
}

.es-status__spinner {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid var(--border-color);
  border-top-color: var(--link-color);
  margin: 0 auto;
  animation: es-spin 0.8s linear infinite;
}

@keyframes es-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .es-status__spinner { animation: none; }
}

.es-status__icon {
  width: 44px;
  height: 44px;
  margin: 0 auto;
  border-radius: 50%;
  background: #fbeae6;
  color: #c0392b;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.es-status .ev-btn--ghost {
  margin-top: 18px;
}

/* ── success state ── */

.es-wrap {
  background: #ffffff;
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 36px 28px 30px;
  text-align: center;
  box-shadow: 0 1px 2px rgba(20, 22, 26, 0.04), 0 10px 26px rgba(20, 22, 26, 0.07);
}

.es-badge {
  width: 56px;
  height: 56px;
  margin: 0 auto 18px;
}

.es-badge__check {
  width: 100%;
  height: 100%;
}

.es-badge__circle {
  fill: #e3f7f4;
  stroke: var(--link-color);
  stroke-width: 2;
}

.es-badge__tick {
  fill: none;
  stroke: var(--link-color-strong);
  stroke-width: 3.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 40;
  stroke-dashoffset: 40;
  animation: es-tick-draw 0.5s 0.15s ease forwards;
}

@keyframes es-tick-draw {
  to { stroke-dashoffset: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .es-badge__tick { animation: none; stroke-dashoffset: 0; }
}

.es-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0 0 6px;
}

.es-subtitle {
  font-size: 14.5px;
  color: var(--secondary-color);
  margin: 0 0 26px;
}

/* ── ticket-style download card ── */

.es-ticket {
  background: var(--page-tint);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  overflow: hidden;
  text-align: left;
}

.es-ticket__top {
  padding: 18px 20px 16px;
}

.es-ticket__eyebrow {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9aa1ab;
  margin-bottom: 6px;
}

.es-ticket__event {
  font-size: 16px;
  font-weight: 700;
  color: #1a1d1f;
  margin: 0;
  line-height: 1.35;
}

.es-perf {
  position: relative;
  height: 1px;
  background: repeating-linear-gradient(
    to right, #d5d8dc 0 6px, transparent 6px 12px
  );
  margin: 0 20px;
}

.es-perf__notch {
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--page-tint);
  transform: translateY(-50%);
}

.es-perf__notch--left { left: -28px; }
.es-perf__notch--right { right: -28px; }

.es-ticket__bottom {
  padding: 18px 20px 20px;
}

/* ── buttons ── */

.ev-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13.5px;
  font-weight: 600;
  border-radius: 9px;
  padding: 11px 18px;
  cursor: pointer;
  border: 1.5px solid transparent;
  text-decoration: none;
  font-family: inherit;
  transition: background 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
}

.es-download-btn {
  width: 100%;
  background: var(--link-color);
  color: #fff;
}

.es-receipt-btn {
	width: 100%;
	margin-bottom: 10px;
	background: var(--surface-raised);
	border-color: var(--border-color);
	color: var(--link-color-strong);
}

.es-receipt-btn:hover {
	background: var(--border-color);
}

.es-download-btn:hover:not(:disabled) {
  background: var(--link-color-strong);
  transform: translateY(-1px);
}

.es-download-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.ev-btn--ghost {
  background: transparent;
  border-color: var(--border-color);
  color: var(--primary-color);
}

.ev-btn--ghost:hover {
  border-color: var(--tertiary-color);
  background: var(--page-tint);
}

/* ── reminder / footnote ── */

.es-reminder {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fef6e7;
  border: 1px solid #f1dfa9;
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 16px;
  font-size: 13px;
  color: #7a5c1e;
  text-align: left;
}

.es-footnote {
  font-size: 12px;
  color: #9aa1ab;
  line-height: 1.6;
  margin: 18px 0 0;
}

.es-delivery-note {
	margin: 0;
	font-size: 13px;
	line-height: 1.6;
	color: #6b7280;
}
`;

const BookSuccess = () => {
	const [searchParams] = useSearchParams();
	const orderId = searchParams.get("orderId");

	const [status, setStatus] = useState("checking");
	const [order, setOrder] = useState(null);
	const [downloading, setDownloading] = useState(false);
	const [downloadError, setDownloadError] = useState(null);
	const [emailStatus, setEmailStatus] = useState("idle");
	const [emailError, setEmailError] = useState(null);
	const pollsRef = useRef(0);
	const timeoutRef = useRef(null);
	const emailAttemptedRef = useRef(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (!orderId) {
			setStatus("error");
			return undefined;
		}

		let cancelled = false;

		const poll = async () => {
			try {
				const res = await fetch(`${API_BASE}/get-order-status?bookOrderId=${orderId}`);
				if (res.status === 404) {
					if (!cancelled) setStatus("error");
					return;
				}
				const data = await res.json();
				if (cancelled) return;

				if (data.status === "paid") {
					setOrder(data);
					setStatus("paid");
					return;
				}
				if (data.status === "failed") {
					setStatus("failed");
					return;
				}

				pollsRef.current += 1;
				setStatus("pending");
				if (pollsRef.current < MAX_POLLS) {
					timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
				} else {
					setStatus("timeout");
				}
			} catch (e) {
				if (!cancelled) setStatus("error");
			}
		};

		poll();

		return () => {
			cancelled = true;
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [orderId]);

	const handleDownload = async () => {
		setDownloading(true);
		setDownloadError(null);
		try {
			// Signed URLs expire quickly on purpose — fetch a fresh one on every
			// click rather than reusing one that might have gone stale.
			const res = await fetch(`${API_BASE}/book-download?orderId=${orderId}`);
			const data = await res.json();
			if (!res.ok) throw new Error(data.message || "Could not get download link.");
			window.location.href = data.url;
		} catch (e) {
			setDownloadError(e.message);
		} finally {
			setDownloading(false);
		}
	};

	const buildReceiptPdf = (order, fallbackId) => {
		const doc = new jsPDF({ unit: "pt", format: "a4" });
		const pageWidth = doc.internal.pageSize.getWidth();
		const marginX = 56;
		const isHardCopy = order.format === "hardCopy";
		const receiptNumber = order.orderId || fallbackId;

		// Header band
		doc.setFillColor(13, 148, 136);
		doc.rect(0, 0, pageWidth, 90, "F");
		doc.setTextColor(255, 255, 255);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(11);
		doc.text("JABU ZWANE", marginX, 40);
		doc.setFontSize(20);
		doc.text("Payment Receipt", marginX, 66);

		let y = 130;
		doc.setTextColor(30, 30, 30);
		doc.setFont("helvetica", "normal");
		doc.setFontSize(10);
		doc.text(`Receipt No: ${receiptNumber}`, marginX, y);
		doc.text(
			`Date: ${new Date().toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })}`,
			pageWidth - marginX,
			y,
			{ align: "right" }
		);

		y += 24;
		doc.setDrawColor(220, 220, 220);
		doc.line(marginX, y, pageWidth - marginX, y);
		y += 30;

		const rows = [
			["Book", order.bookTitle || "—"],
			["Format", isHardCopy ? "Hard copy" : "Online PDF"],
			["Customer", order.buyerName || "—"],
			["Email", order.buyerEmail || "—"],
		];
		if (order.buyerPhone) rows.push(["Cellphone", order.buyerPhone]);
		if (isHardCopy && order.buyerAddress) rows.push(["Delivery address", order.buyerAddress]);

		rows.forEach(([label, value]) => {
			doc.setFont("helvetica", "normal");
			doc.setTextColor(110, 120, 120);
			doc.setFontSize(10);
			doc.text(label, marginX, y);
			doc.setFont("helvetica", "bold");
			doc.setTextColor(30, 30, 30);
			doc.setFontSize(11);
			const lines = doc.splitTextToSize(String(value), pageWidth - marginX * 2 - 160);
			doc.text(lines, marginX + 160, y);
			y += 16 * lines.length + 6;
		});

		y += 10;
		doc.setDrawColor(220, 220, 220);
		doc.line(marginX, y, pageWidth - marginX, y);
		y += 34;

		const amount = new Intl.NumberFormat("en-ZA", {
			style: "currency",
			currency: order.currency || "ZAR",
		}).format(order.price || 0);

		doc.setFont("helvetica", "bold");
		doc.setTextColor(110, 120, 120);
		doc.setFontSize(11);
		doc.text("Total paid", marginX, y);
		doc.setTextColor(13, 148, 136);
		doc.setFontSize(18);
		doc.text(amount, pageWidth - marginX, y, { align: "right" });

		y += 44;
		doc.setFont("helvetica", "normal");
		doc.setTextColor(140, 140, 140);
		doc.setFontSize(9);
		const footer = isHardCopy
			? "Thank you for your purchase. We will deliver your hard copy to the address provided and contact you on the cellphone number supplied."
			: "Thank you for your purchase. Your digital book download is available from the confirmation page.";
		doc.text(doc.splitTextToSize(footer, pageWidth - marginX * 2), marginX, y);

		return doc;
	};

	// inside the component, replacing the old downloadReceipt:
	const downloadReceipt = () => {
		if (!order) return;
		const doc = buildReceiptPdf(order, orderId);
		doc.save(`jabu-zwane-receipt-${order.orderId || orderId}.pdf`);
	};

	const sendPurchaseEmail = async () => {
		if (!order || emailAttemptedRef.current) return;
		const storageKey = `jz-book-purchase-email-${order.orderId || orderId}`;
		try {
			if (window.localStorage.getItem(storageKey) === "sent") {
				setEmailStatus("sent");
				return;
			}
		} catch (err) {
			console.warn("Could not check the book email delivery marker:", err);
		}

		emailAttemptedRef.current = true;
		setEmailStatus("sending");
		setEmailError(null);
		try {
			await sendBookPurchaseConfirmation(order, orderId);
			try {
				window.localStorage.setItem(storageKey, "sent");
			} catch (err) {
				console.warn("Could not save the book email delivery marker:", err);
			}
			setEmailStatus("sent");
		} catch (err) {
			console.error("Book purchase confirmation email failed:", err);
			setEmailError(err.message || "Could not send the confirmation email.");
			setEmailStatus("failed");
		}
	};

	useEffect(() => {
		if (status === "paid" && order) sendPurchaseEmail();
		// sendPurchaseEmail uses the latest order from this effect's render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, order]);

	const retryPurchaseEmail = () => {
		emailAttemptedRef.current = false;
		sendPurchaseEmail();
	};

	// generate it automatically once, right when payment is confirmed
	const autoReceiptRef = useRef(false);
	useEffect(() => {
		if (status === "paid" && order && !autoReceiptRef.current) {
			autoReceiptRef.current = true;
			downloadReceipt();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, order]);

	const retry = () => window.location.reload();

	return (
		<>
			<Helmet>
				<title>{`Your Book | ${INFO.main.title}`}</title>
			</Helmet>

			<style>{ES_CSS}</style>

			<div className="page-content">
				<NavBar active="articles" />

				<div className="content-wrapper">
					<section className="ev-page es-page">
						<div className="ev-container es-container">
							{status === "checking" && (
								<div className="es-status">
									<div className="es-status__spinner" aria-hidden="true" />
									<h2>Confirming your payment…</h2>
									<p>Just a moment while we check with Yoco.</p>
								</div>
							)}

							{status === "pending" && (
								<div className="es-status">
									<div className="es-status__spinner" aria-hidden="true" />
									<h2>Payment received</h2>
									<p>Preparing your order now — this only takes a few seconds.</p>
								</div>
							)}

							{status === "paid" && order && (
								<div className="es-wrap">
									<div className="es-badge">
										<svg viewBox="0 0 52 52" className="es-badge__check">
											<circle cx="26" cy="26" r="25" className="es-badge__circle" />
											<path d="M14 27l8 8 16-16" className="es-badge__tick" />
										</svg>
									</div>

									<h1 className="es-title">Thank you! 📚</h1>
									<p className="es-subtitle">{order.format === "hardCopy" ? "Your hard copy order has been confirmed." : "Your book is ready to download below."}</p>
									<p role="status" aria-live="polite">
										{emailStatus === "sending" && "Sending your confirmation email…"}
										{emailStatus === "sent" && `Confirmation email sent to ${order.buyerEmail}.`}
										{emailStatus === "failed" && (
											<>We couldn't send your confirmation email ({emailError}). <button type="button" onClick={retryPurchaseEmail}>Try again</button></>
										)}
									</p>

									<div className="es-ticket">
										<div className="es-ticket__top">
											<span className="es-ticket__eyebrow">Book</span>
											<h2 className="es-ticket__event">{order.bookTitle}</h2>
										</div>

										<div className="es-perf" aria-hidden="true">
											<span className="es-perf__notch es-perf__notch--left" />
											<span className="es-perf__notch es-perf__notch--right" />
										</div>

										<div className="es-ticket__bottom">
											<button type="button" className="ev-btn es-receipt-btn" onClick={downloadReceipt}>
												Download receipt
											</button>
											{order.format === "hardCopy" ? (
												<p className="es-delivery-note">We will deliver your hard copy to the address provided at checkout and contact you on the cellphone number supplied.</p>
											) : <button
												type="button"
												className="ev-btn es-download-btn"
												onClick={handleDownload}
												disabled={downloading}
											>
												{downloading ? "Preparing…" : "⬇ Download your book (PDF)"}
											</button>}
										</div>
									</div>

									{downloadError && (
										<div className="es-reminder">
											<span className="es-reminder__icon">⚠️</span>
											<span>{downloadError}</span>
										</div>
									)}

									<p className="es-footnote">
										{order.format === "hardCopy"
											? "Keep your receipt for your records. We will contact you about delivery."
											: "The download link expires a few minutes after it's generated. You can return to this page and download again."}
									</p>
								</div>
							)}

							{status === "failed" && (
								<div className="es-status es-status--error">
									<div className="es-status__icon">✕</div>
									<h2>Payment didn't go through</h2>
									<p>You haven't been charged for this book.</p>
									<Link className="ev-btn ev-btn--ghost" to="/articles">Back to books</Link>
								</div>
							)}

							{status === "timeout" && (
								<div className="es-status">
									<div className="es-status__icon">⏱</div>
									<h2>Still confirming</h2>
									<p>This is taking longer than usual — your payment provider may be slow to respond.</p>
									<button className="ev-btn ev-btn--ghost" onClick={retry}>Check again</button>
								</div>
							)}

							{status === "error" && (
								<div className="es-status es-status--error">
									<div className="es-status__icon">?</div>
									<h2>We couldn't find this order</h2>
									<p>If you were charged, please contact us with your payment reference.</p>
									<Link className="ev-btn ev-btn--ghost" to="/articles">Back to books</Link>
								</div>
							)}
						</div>
					</section>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</>
	);
};

export default BookSuccess;